<template>
  <core-view>
    <div class="portariachecklist-header">
      <div class="portariachecklist-header-total">
        <span class="portariachecklist-total-label">Total Registros</span>
        <span class="portariachecklist-total-numero">{{ registrosFiltrados.length }}</span>
      </div>
    </div>

    <!-- Aviso de envios com conflito/falha (somem do filtro padrão). -->
    <v-alert
      v-if="totalComProblema && !['conflito', 'falha'].includes(filtroSituacaoAtual)"
      class="portariachecklist-aviso"
      type="warning"
      dense
      text
    >
      <div class="portariachecklist-aviso-conteudo">
        <span>
          {{ totalComProblema }}
          {{ totalComProblema === 1 ? 'checklist com problema' : 'checklists com problema' }}
          no envio
        </span>
        <v-btn small text color="warning" @click="$_verProblemas">Ver</v-btn>
      </div>
    </v-alert>

    <div class="portariachecklist-loading" v-if="loading">
      <v-progress-circular indeterminate color="primary" size="40" />
    </div>

    <div class="portariachecklist-lista" v-else-if="registrosFiltrados.length">
      <v-card
        class="portariachecklist-card"
        :class="`portariachecklist-card--${item.situacao}`"
        v-for="item in registrosFiltrados"
        :key="item.id"
        rounded="lg"
        elevation="2"
      >
        <div class="portariachecklist-card-faixa" :style="{ backgroundColor: $_corSituacao(item.situacao) }"></div>

        <div class="portariachecklist-card-conteudo">
          <div class="portariachecklist-card-topo">
            <v-avatar class="portariachecklist-card-icone" :color="$_corSituacao(item.situacao)" size="44">
              <v-icon dark>{{ $_iconeOperacao(item.operacao) }}</v-icon>
            </v-avatar>

            <div class="portariachecklist-card-placa-bloco">
              <span class="portariachecklist-card-placa">{{ item.titulo }}</span>
              <span class="portariachecklist-card-data">{{ $_formataData(item.entrada || item.cadastro) }}</span>
            </div>

            <v-chip
              small
              v-if="item.situacao"
              :color="$_corSituacao(item.situacao)"
              text-color="white"
              class="portariachecklist-card-status"
            >
              {{ $_descricaoSituacao(item.situacao) }}
            </v-chip>
          </div>

          <v-divider class="my-3"></v-divider>

          <!-- Linhas livres de `card.resumo`; o `cabecalho` completo fica dentro do checklist. -->
          <div class="portariachecklist-card-info">
            <div
              class="portariachecklist-card-info-item"
              v-for="campo in item.resumo"
              :key="campo.rotulo"
            >
              <span class="portariachecklist-card-info-rotulo">{{ campo.rotulo }}</span>
              <span>{{ campo.valor }}</span>
            </div>
            <div class="portariachecklist-card-info-item">
              <v-icon small color="grey darken-1">mdi-clock-outline</v-icon>
              <span>Espera: {{ $_tempoEspera(item.entrada || item.cadastro) }}</span>
            </div>
            <div class="portariachecklist-card-info-item" v-if="$_situacaoFormulario(item) === 'finalizado'">
              <v-icon small color="green darken-1">mdi-check-decagram-outline</v-icon>
              <span>
                Finalizado em {{ $_formataData(item.formulario.fordatafinalizado) }}
                <template v-if="item.formulario.foinome">por {{ item.formulario.foinome }}</template>
              </span>
            </div>
          </div>

          <div class="portariachecklist-card-rodape">
            <div class="portariachecklist-card-rodape-chips">
              <v-chip
                x-small
                outlined
                v-if="item.operacao"
                :color="item.operacao === 'carga' ? 'orange darken-2' : 'blue darken-2'"
              >
                {{ item.operacao === 'carga' ? 'Carga' : 'Descarga' }}
              </v-chip>

              <v-chip
                x-small
                :color="$_corFormulario(item)"
                text-color="white"
              >
                <v-icon left x-small>{{ $_iconeFormulario(item) }}</v-icon>
                {{ $_descricaoFormulario(item) }}
              </v-chip>
            </div>

            <v-btn
              class="portariachecklist-card-checklist"
              v-if="!['finalizado', 'finalizado-pendente'].includes($_situacaoFormulario(item))"
              color="primary"
              small
              depressed
              @click="$_abrirChecklist(item)"
            >
              <v-icon left small>mdi-clipboard-check-outline</v-icon>
              {{ $_textoBotaoChecklist(item) }}
            </v-btn>
          </div>
        </div>
      </v-card>
    </div>

    <p class="portariachecklist-vazio" v-else-if="!loading">Nenhum caminhão encontrado para os filtros selecionados.</p>

    <v-dialog v-model="dialogChecklist" fullscreen hide-overlay transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary" dense>
          <v-btn icon dark @click="dialogChecklist = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>{{ itemSelecionado?.formulario?.fornome ?? 'Checklist' }}</v-toolbar-title>
        </v-toolbar>
        <div class="checklist-container">
          <checklist
            v-if="dialogChecklist && chkFormularioId"
            :id="chkFormularioId"
            modo="dexie-first"
            :api="chkApi"
            :cabecalho="chkCabecalho"
            :useidusuario="chkUseidusuario"
            :icone="CHECKLIST_ICONE"
            @enviado="onChecklistEnviado"
            @concluido="onChecklistConcluido"
            @erro="onChecklistErro"
          />
        </div>
      </v-card>
    </v-dialog>
  </core-view>
</template>

<script>
import moment from 'moment';
import { mapActions } from 'vuex';

import CoreView from '@/components/core/view/Index.vue';
import Checklist from '@officeline-libs/checklist';
import '@officeline-libs/checklist/dist/checklist.css';
import { PORTARIA_CHECKLIST_QUERIES } from '@/constants/QUERY_FILTERS';
import { USER_STORAGE, PORTARIA_CHECKLIST_STORAGE } from '@/constants/STORAGE';
import criaChecklistOffline from '@/utils/local/checklistOffline';
import { SetupEnvioLocal } from '@/utils/atualizalocal';

// Só para URL de capa/anexos no servidor; dados vêm do Dexie.
// TODO: trocar por setupBaseURL() quando a API definitiva estiver disponível.
const API_CHECKLIST = `${window.location.protocol}//${window.location.hostname}:3006`;

// ÚNICO ponto que conhece o formato do snapshot da origem. GET /formulario/fila devolve
// uma linha de `formulario` (não mais de portariacargadescarga) com os dados de exibição
// dentro de `fororigemdados` (json), em dois blocos de papéis opostos:
//
// - `card`: o que esta tela INTERPRETA (titulo, situacao, operacao, entrada, cadastro).
//   Os mapas de cor/ícone/rótulo abaixo dependem desses valores. `card.resumo` são as
//   linhas livres do card (rótulo, valor e ordem de quem envia o JSON);
// - `cabecalho`: campos livres definidos por quem alimenta a API. Nem os rótulos nem a
//   quantidade são conhecidos aqui - não entram no card, só são repassados ao checklist.
//
// ATENÇÃO: fororigemdados é um SNAPSHOT do momento em que o ERP gerou o formulário. Se o
// caminhão sair da empresa depois, `situacao` só muda se o ERP reescrever o campo.
function normalizaRegistro(formulario) {
  const dados = formulario.fororigemdados ?? {};
  const card = dados.card ?? {};

  return {
    id: formulario.id,
    titulo: card.titulo,
    operacao: card.operacao,
    situacao: card.situacao,
    entrada: card.entrada,
    cadastro: card.cadastro ?? formulario.fordatacadastro,
    cabecalho: dados.cabecalho ?? {},
    // Na ordem enviada; valor nulo ou vazio não vira linha.
    resumo: Object.entries(card.resumo ?? {})
      .filter(([, valor]) => ![null, undefined, ''].includes(valor))
      .map(([rotulo, valor]) => ({ rotulo, valor })),
    // Situação do envio local (null = nada pendente).
    envioSituacao: formulario.envioSituacao ?? null,
    envioErro: formulario.envioErro ?? null,
    formulario: {
      id: formulario.id,
      fornome: formulario.fornome,
      fordatafinalizado: formulario.fordatafinalizado,
      foiultimagravacao: formulario.foiultimagravacao,
      foinome: formulario.foinome,
      forlink: formulario.forlink,
    },
  };
}

function hoje() {
  return new Date().toISOString().substring(0, 10);
}

const INTERVALO_ATUALIZACAO_MS = 60000;

// Ícone exibido ao lado do título dentro do checklist. Prop opcional do componente:
// sem valor, ele não renderiza ícone nenhum.
const CHECKLIST_ICONE = 'mdi-clipboard-check-outline';

export default {
  name: 'ViewPortariaChecklist',

  components: {
    CoreView,
    Checklist,
  },

  data() {
    return {
      registros: [],
      loading: false,
      intervalId: null,
      visibilityHandler: null,
      dialogChecklist: false,
      itemSelecionado: null,
      // Provider dexie-first.
      chkApi: criaChecklistOffline({ baseURL: API_CHECKLIST }),
      CHECKLIST_ICONE,
    };
  },

  computed: {
    filtroDataDe() {
      return this.$route.query[PORTARIA_CHECKLIST_QUERIES.createdFrom] || hoje();
    },

    filtroDataAte() {
      return this.$route.query[PORTARIA_CHECKLIST_QUERIES.createdBefore] || hoje();
    },

    filtroMovimento() {
      return this.$route.query[PORTARIA_CHECKLIST_QUERIES.movimento] || '';
    },

    registrosFiltrados() {
      const filtroOperacao = this.$route.query[PORTARIA_CHECKLIST_QUERIES.operacao] || '';
      const filtroSituacaoFormulario = this.$route.query[PORTARIA_CHECKLIST_QUERIES.situacaoFormulario] || '';

      return this.registros.filter((item) => {
        const passaOperacao = !filtroOperacao || filtroOperacao === item.operacao;
        const situacao = this.$_situacaoFormulario(item);

        // Sem filtro: só pendentes (finalizados somem da fila).
        const passaSituacaoFormulario = filtroSituacaoFormulario
          ? situacao === filtroSituacaoFormulario
          : ['nao-iniciado', 'andamento'].includes(situacao);

        return passaOperacao && passaSituacaoFormulario;
      });
    },

    // Envios com conflito ou recusa definitiva.
    totalComProblema() {
      return this.registros.filter((item) => ['conflito', 'falha'].includes(item.envioSituacao)).length;
    },

    filtroSituacaoAtual() {
      return this.$route.query[PORTARIA_CHECKLIST_QUERIES.situacaoFormulario] || '';
    },

    chkFormularioId() {
      return this.itemSelecionado?.formulario?.id ?? null;
    },

    // SÓ exibição: cada chave vira um par rótulo/valor no cabeçalho do checklist.
    // São os mesmos campos livres que a API devolve em `cabecalho` quando o formulário
    // é aberto pelo link público (FormularioService.preparaFormulario) - aqui só
    // evitamos a ida extra à API, já que a listagem trouxe o snapshot junto.
    chkCabecalho() {
      return this.itemSelecionado?.cabecalho ?? {};
    },

    // Usuário logado no app (não o motorista/visitante) - a API usa isso pra preencher
    // nome/cpf da identificação do formulário automaticamente. Vai em prop separada:
    // dentro de `cabecalho` ele era renderizado como se fosse um campo de exibição.
    chkUseidusuario() {
      return sessionStorage.getItem(USER_STORAGE.useidusuario) || null;
    },
  },

  methods: {
    ...mapActions({
      $_findPortariaChecklist: 'portariachecklist/find',
    }),

    async $_carregarRegistros({ silencioso } = {}) {
      if (!silencioso) this.loading = true;

      try {
        // Lê do Dexie: funciona com ou sem conexão.
        const data = await this.$_findPortariaChecklist({
          createdFrom: this.filtroDataDe,
          createdBefore: this.filtroDataAte,
          movimento: this.filtroMovimento,
        });
        this.registros = data.map(normalizaRegistro);
      } catch (err) {
        console.error('[portariachecklist] erro ao buscar registros', err);
      } finally {
        this.loading = false;
      }
    },

    // A query some ao sair do menu: devolve à URL o último filtro aplicado (Filters.vue).
    async $_restaurarFiltros() {
      const query = { ...this.$route.query };
      Object.entries(PORTARIA_CHECKLIST_QUERIES).forEach(([campo, chave]) => {
        const salvo = sessionStorage.getItem(PORTARIA_CHECKLIST_STORAGE[campo]);
        if (salvo && !(chave in query)) query[chave] = salvo;
      });

      if (Object.keys(query).length !== Object.keys(this.$route.query).length) {
        await this.$router.replace({ query }).catch(() => {});
      }
    },

    $_iniciarAtualizacaoAutomatica() {
      this.$_pararAtualizacaoAutomatica();

      this.intervalId = setInterval(() => {
        this.$_carregarRegistros({ silencioso: true });
      }, INTERVALO_ATUALIZACAO_MS);
    },

    $_pararAtualizacaoAutomatica() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },

    $_abrirChecklist(item) {
      this.itemSelecionado = item;
      this.dialogChecklist = true;

      // Empilha um hash na rota só para o checklist: assim o botão nativo de
      // "voltar" (navegador ou mobile) fecha o checklist e retorna pra fila,
      // em vez de sair da tela.
      this.$router.push({
        path: this.$route.path,
        query: this.$route.query,
        hash: '#checklist',
      }).catch(() => {});
    },

    $_formataData(data) {
      return moment(data).format('DD/MM/YYYY - HH:mm');
    },

    $_tempoEspera(pcdentrada) {
      const minutos = moment().diff(moment(pcdentrada), 'minutes');
      const horas = Math.floor(minutos / 60);
      const restante = minutos % 60;

      return `${horas}:${String(restante).padStart(2, '0')}`;
    },

    $_descricaoSituacao(situacao) {
      return { A: 'Aguardando Entrada', D: 'Dentro da Empresa', F: 'Saiu da Empresa' }[situacao] ?? situacao;
    },

    $_corSituacao(situacao) {
      return { A: 'grey darken-1', D: 'orange darken-1', F: 'green darken-1' }[situacao] ?? 'grey';
    },

    $_iconeOperacao(operacao) {
      return operacao === 'carga' ? 'mdi-truck-fast-outline' : 'mdi-truck-delivery-outline';
    },

    // Estados do checklist + estados do envio local (finalizado-pendente, conflito 409,
    // falha 422/413).
    $_situacaoFormulario(item) {
      if (!item.formulario) return null;
      if (item.envioSituacao === 'conflito') return 'conflito';
      if (item.envioSituacao === 'falha') return 'falha';
      if (item.envioSituacao === 'finalizado-pendente') return 'finalizado-pendente';
      if (item.formulario.fordatafinalizado) return 'finalizado';
      if (item.formulario.foiultimagravacao) return 'andamento';
      return 'nao-iniciado';
    },

    $_descricaoFormulario(item) {
      return {
        finalizado: 'Checklist Finalizado',
        'finalizado-pendente': 'Finalizado - Aguardando Envio',
        conflito: 'Conflito no Envio',
        falha: 'Falha no Envio',
        andamento: 'Checklist em Andamento',
        'nao-iniciado': 'Checklist Não Iniciado',
      }[this.$_situacaoFormulario(item)];
    },

    $_corFormulario(item) {
      return {
        finalizado: 'green darken-1',
        'finalizado-pendente': 'teal darken-1',
        conflito: 'deep-orange darken-2',
        falha: 'red darken-2',
        andamento: 'blue darken-1',
        'nao-iniciado': 'grey darken-1',
      }[this.$_situacaoFormulario(item)];
    },

    $_iconeFormulario(item) {
      return {
        finalizado: 'mdi-check-circle-outline',
        'finalizado-pendente': 'mdi-cloud-upload-outline',
        conflito: 'mdi-alert-octagon-outline',
        falha: 'mdi-cloud-alert',
        andamento: 'mdi-progress-clock',
        'nao-iniciado': 'mdi-circle-outline',
      }[this.$_situacaoFormulario(item)];
    },

    $_textoBotaoChecklist(item) {
      return {
        andamento: 'Finalizar Formulário',
        'nao-iniciado': 'Responder Questionário',
        conflito: 'Ver Respostas',
        falha: 'Ver Respostas',
      }[this.$_situacaoFormulario(item)];
    },

    $_verProblemas() {
      this.$router.push({
        path: this.$route.path,
        query: { ...this.$route.query, [PORTARIA_CHECKLIST_QUERIES.situacaoFormulario]: 'conflito' },
      }).catch(() => {});
    },

    onChecklistEnviado(payload) {
      console.log('[checklist] gravado no dispositivo', payload);
      // Tenta subir já; offline sobe no próximo ciclo.
      SetupEnvioLocal();
    },

    onChecklistConcluido(payload) {
      console.log('[checklist] concluído', payload);
      SetupEnvioLocal();
      // Fechar o dialog aciona o watcher de `dialogChecklist`, que remove o hash e,
      // por consequência, o watcher de `$route.hash` recarrega os registros.
      this.dialogChecklist = false;
    },

    onChecklistErro(msg) {
      console.log('[checklist] erro', msg);
    },
  },

  watch: {
    filtroDataDe() {
      this.$_carregarRegistros();
    },

    filtroDataAte() {
      this.$_carregarRegistros();
    },

    filtroMovimento() {
      this.$_carregarRegistros();
    },

    // Fechou o checklist por qualquer via (botão X, ESC, @concluido) e o hash ainda
    // está lá: volta no histórico pra tirar o hash. Quem efetivamente recarrega os
    // registros é o watcher de `$route.hash`, abaixo.
    dialogChecklist(aberto) {
      if (!aberto && this.$route.hash === '#checklist') {
        this.$router.back();
      }
    },

    // Dispara tanto quando o usuário fecha o checklist pela UI (via watcher acima,
    // que já tirou o hash indo pra trás) quanto quando ele aperta o "voltar" nativo
    // do navegador/celular diretamente - único lugar que efetivamente busca os
    // dados atualizados do formulário (ex.: respostas salvas antes de fechar).
    '$route.hash'(hash) {
      if (hash !== '#checklist') {
        this.dialogChecklist = false;
        this.itemSelecionado = null;
        this.$_carregarRegistros({ silencioso: true });
      }
    },
  },

  async created() {
    SetupEnvioLocal();
    await this.$_restaurarFiltros();
    this.$_carregarRegistros();

    this.visibilityHandler = () => {
      if (document.hidden) {
        this.$_pararAtualizacaoAutomatica();
      } else {
        this.$_carregarRegistros({ silencioso: true });
        this.$_iniciarAtualizacaoAutomatica();
      }
    };

    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.$_iniciarAtualizacaoAutomatica();
  },

  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.visibilityHandler);
    this.$_pararAtualizacaoAutomatica();
  },
};
</script>

<style scoped>
.portariachecklist-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px;
}

.portariachecklist-header-total {
  display: flex;
  flex-direction: column;
}

.portariachecklist-total-label {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.85rem;
}

.portariachecklist-total-numero {
  font-size: 1.6rem;
  font-weight: 700;
}

.portariachecklist-aviso {
  margin: 0 16px 12px;
}

.portariachecklist-aviso-conteudo {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
}

.portariachecklist-loading {
  display: flex;
  justify-content: center;
  padding: 48px;
}

.portariachecklist-lista {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
  padding: 0 16px 24px;
}

.portariachecklist-card {
  display: flex;
  overflow: hidden;
}

.portariachecklist-card-faixa {
  width: 6px;
  flex-shrink: 0;
}

.portariachecklist-card-conteudo {
  flex: 1;
  padding: 16px;
  min-width: 0;
}

.portariachecklist-card-topo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.portariachecklist-card-placa-bloco {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}

.portariachecklist-card-placa {
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.portariachecklist-card-data {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.55);
}

.portariachecklist-card-status {
  flex-shrink: 0;
}

.portariachecklist-card-info {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.portariachecklist-card-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88rem;
}

.portariachecklist-card-info-item span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.portariachecklist-card-info-rotulo {
  color: rgba(0, 0, 0, 0.55);
  flex-shrink: 0;
}

.portariachecklist-card-info-rotulo::after {
  content: ':';
}

.portariachecklist-card-rodape {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 14px;
  gap: 8px;
}

.portariachecklist-card-rodape-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.portariachecklist-vazio {
  text-align: center;
  color: rgba(0, 0, 0, 0.5);
  padding: 32px;
}

.checklist-container {
  padding: 16px 0 48px;
  height: calc(100vh - 48px);
  overflow-y: auto;
  background-color: #f5f5f5;
}
</style>
